import html2canvas from 'html2canvas';

// Een kaart (grafiek + cijfers) als afbeelding naar het klembord, om in
// PowerPoint of een mail te plakken. Updateverzoek Groentjessoep (Diana),
// 2026-10-26: 'visualisatie kopiëren, met de data erbij'.

/**
 * Zet de kloon van een kaart klaar voor het plaatje. Draait op de kloon die
 * html2canvas maakt (`onclone`), nooit op de pagina zelf.
 *
 * - De cijfertabel onder een grafiek (ChartDataTable) staat op het scherm
 *   verborgen; in de kopie hoort hij erbij.
 * - De kaartinhoud scrolt en vult de kaart; zonder dit valt alles onder de
 *   vouw buiten beeld.
 * - Bediening ('Aan dashboard', bladerpijlen, het speldje) hoort niet in een
 *   plaatje voor PowerPoint. Knoppen gaan er altijd uit; wat geen knop is maar
 *   wel bediening, draagt `data-copy-hide`.
 *
 * @param {HTMLElement} root - De gekloonde kaartinhoud
 * @returns {void}
 */
/**
 * Laat een vak meegroeien met zijn inhoud in plaats van de kaarthoogte te vullen.
 *
 * @param {HTMLElement} el
 * @returns {void}
 */
const growWithContent = (el) => {
    // Met voorrang: kaartstijlen zetten een hoogte soms met !important
    // (`.housevisits-card-chart > div { height: 100% !important }`), en dan
    // wint een gewone inline stijl niet. Het gaat om de kloon, niet de pagina.
    el.style.setProperty('max-height', 'none', 'important');
    el.style.setProperty('height', 'auto', 'important');
    el.style.setProperty('overflow', 'visible', 'important');
    // flex: 1 (1 1 0%) houdt een vak op de kaarthoogte, ook met height auto.
    el.style.setProperty('flex', 'none', 'important');
};

export const revealForCopy = (root) => {
    root.querySelectorAll('.chart-data-table').forEach(table => {
        table.hidden = false;
        // De vakken tussen de kaart en de tabel vullen de kaarthoogte
        // (flex: 1, height: 100%); zonder dit valt de tabel onder de grafiek
        // buiten het plaatje, ook als de kaart zelf mag groeien (2026-10-28).
        for (let el = table.parentElement; el && el !== root; el = el.parentElement) growWithContent(el);
    });
    // Het grafiekvak (height: 100%) wordt anders zo hoog als het vak dat net
    // mocht meegroeien, centreert de grafiek daarin en duwt de tabel eronder
    // buiten het plaatje. De grafiek zelf heeft een vaste maat in pixels.
    root.querySelectorAll('.graph-wrapper').forEach(growWithContent);
    root.querySelectorAll('button, [role="button"], [data-copy-hide]').forEach(el => el.remove());
    growWithContent(root);
};

/** Lege toestanden van een kaart: DashboardCardEmptyState en die van Analyseren. */
const EMPTY_STATE = '.dashboard-empty-state, .analysis-card-empty';
/** Alles wat wél iets laat zien: een grafiek of een tabel. */
const CONTENT = '.recharts-wrapper, table';

/**
 * Of een kaart alleen een lege toestand toont. Dan valt er niets te kopiëren:
 * in de presentatie stond anders een leeg vak terwijl de knop 'Gekopieerd'
 * meldde (testronde 2026-10-28). Staat er naast de lege toestand een grafiek
 * of tabel (een ander meetmoment met gegevens), dan is er wél iets te zien.
 *
 * @param {HTMLElement} root - De kaartinhoud
 * @returns {boolean}
 */
export const hasNothingToCopy = (root) => (
    !!root.querySelector(EMPTY_STATE) && !root.querySelector(CONTENT)
);

/**
 * @typedef {object} CopyDeps
 * @property {(el: HTMLElement, options: object) => Promise<HTMLCanvasElement>} [render] - Standaard html2canvas
 * @property {{ write: (items: Array<ClipboardItem>) => Promise<void> }} [clipboard] - Standaard navigator.clipboard
 */

/**
 * Maakt een PNG van een element en zet die op het klembord.
 *
 * De ClipboardItem krijgt een Promise en wordt meteen weggeschreven: Safari
 * staat schrijven naar het klembord alleen toe binnen de klik zelf, en het
 * tekenen duurt langer dan dat venster.
 *
 * Gooit als het niet lukt — geen stille terugval op een download: de knop
 * vertelt de gebruiker dat het kopiëren mislukte.
 *
 * @param {HTMLElement} element - Wat er gekopieerd wordt
 * @param {CopyDeps} [deps]
 * @returns {Promise<void>}
 */
const copyElementAsImage = async (element, { render = html2canvas, clipboard = navigator.clipboard } = {}) => {
    if (typeof ClipboardItem === 'undefined' || !clipboard?.write) {
        throw new Error('Deze browser kan geen afbeelding naar het klembord kopiëren.');
    }

    const blob = render(element, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        onclone: (_doc, clonedElement) => revealForCopy(clonedElement),
    }).then(canvas => new Promise((resolve, reject) => {
        canvas.toBlob(png => (png ? resolve(png) : reject(new Error('Er kon geen afbeelding gemaakt worden.'))), 'image/png');
    }));

    // eslint-disable-next-line no-undef
    const item = new ClipboardItem({ 'image/png': blob });
    // Beide afwachten: een fout bij het tekenen moet ook boven komen, ook als
    // de browser het schrijven al geaccepteerd had.
    await Promise.all([clipboard.write([item]), blob]);
};

export default copyElementAsImage;
